// Shared helpers for the custom dashboard.
//
// Keep this a leaf module: both the widget components and the wizard steps
// import it, so it must not import either of them back.

// The palette vocabulary shared by every widget type. Each widget maps these
// names onto its own classes — only the names are shared, never the styles.
export const PALETTE_OPTIONS = ['primary', 'secondary', 'simple'];

// Bucket sizes, in seconds, matching the resolutions the analyze charts offer
// (see lmit.wise.chart.control.resolution.js).
export const RESOLUTIONS = [900, 3600, 86400, 2592000, 31536000];

// The periods a chart widget can be plotted over.
export const CHART_PERIODS = ['last_24h', 'last_7d', 'last_30d', 'last_12m', 'wtd', 'mtd', 'ytd'];

export const CHART_TYPES = ['column', 'line'];

// The bucket size a period is plotted at unless the widget carries its own.
export const DEFAULT_RATE_BY_PERIOD = {
    last_12m: 2592000,
    last_24h: 3600,
    last_30d: 86400,
    last_7d: 86400,
    mtd: 86400,
    wtd: 86400,
    ytd: 2592000
};

// The longest each period runs, the to-date ones once they are complete, so a
// chart type picked early in the year still suits it at the end.
const PERIOD_SECONDS = {
    last_12m: 365 * 86400,
    last_24h: 86400,
    last_30d: 30 * 86400,
    last_7d: 7 * 86400,
    mtd: 31 * 86400,
    wtd: 7 * 86400,
    ytd: 365 * 86400
};

// Up to a month of days reads well as bars; past that the bars crowd together
// and a line reads better.
const MAX_BAR_POINTS = 31;

export function recommendedChartType({ period, rate }) {
    const seconds = PERIOD_SECONDS[period];

    if (!seconds) {
        return CHART_TYPES[0];
    }

    return seconds / (rate || DEFAULT_RATE_BY_PERIOD[period]) <= MAX_BAR_POINTS ? 'column' : 'line';
}

// The api takes whole days: it expands each date in the resource's timezone.
export const API_DATE_FORMAT = 'YYYY-MM-DD';

// Period bounds for a chart widget, resolved in the *site's* timezone: a bar
// labelled "March" should start when March started at the site, not in whatever
// timezone the browser happens to be in. The api has no time of day, so
// last_24h is yesterday and today.
export function chartPeriodInterval(period, timezone) {
    const to = moment.tz(timezone);
    let from;

    switch (period) {
        case 'last_7d':
            from = to.clone().subtract(7, 'days');
            break;
        case 'last_30d':
            from = to.clone().subtract(30, 'days');
            break;
        case 'last_12m':
            from = to.clone().subtract(12, 'months');
            break;
        case 'wtd':
            from = to.clone().startOf('week');
            break;
        case 'mtd':
            from = to.clone().startOf('month');
            break;
        case 'ytd':
            from = to.clone().startOf('year');
            break;
        case 'last_24h':
        default:
            from = to.clone().subtract(1, 'day');
            break;
    }

    return {
        from: from.format(API_DATE_FORMAT),
        to: to.format(API_DATE_FORMAT)
    };
}

// The period ends today: the day is whole on the api side, and there is no data past now anyway.
export const datesInterval = function(period, timezone) {
    const to = moment().tz(timezone);
    const from = to.clone();

    switch (period) {
        case 'wtd':
            from.startOf('week');
            break;
        case 'mtd':
            from.startOf('month');
            break;
        case 'ytd':
            from.startOf('year');
            break;
    }

    return { from: from.format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD') };
};

// The text on the secondary (dark green) cards, a softer white: Tailwind's gray-200,
// `tw-text-gray-200` in the widgets' classes.
export const SECONDARY_TEXT = '#e5e7eb';

// Read at call time: WiseMetering.baseColors is installed by the legacy bundle,
// which may not have run at import time.
export function chartColors(palette) {
    const { dark_green, green } = WiseMetering.baseColors;

    switch (palette) {
        // `green` is the theme's primary colour.
        case 'secondary':
            return { grid: 'rgba(255, 255, 255, 0.2)', series: green, text: SECONDARY_TEXT };
        case 'simple':
            return { grid: 'rgba(44, 44, 44, 0.15)', series: green, text: dark_green };
        case 'primary':
        default:
            return { grid: 'rgba(34, 76, 79, 0.25)', series: dark_green, text: dark_green };
    }
}

// The fewest decimals that still tell every tick apart, so round ticks don't
// print trailing zeros ("3.000.000,00 Wh") that get cut off in narrow widgets.
export function axisDecimals(ticks = [], unit, decimals, unitScale) {
    const max = Number(decimals) || 0;

    for (let candidate = 0; candidate < max; candidate++) {
        const labels = ticks.map(tick => formatValue(tick, unit, candidate, unitScale));

        if (new Set(labels).size === labels.length) {
            return candidate;
        }
    }

    return max;
}

export const OBJECTIVE_PERIODS = ['ytd', 'mtd'];

// Energy targets are entered in kWh, but `real` arrives in Wh.
export function objectiveTargets(objective) {
    const factor = objective.isKwh() ? 1000 : 1;

    return objective.getTargetDataArray().map(value => value * factor);
}

export function objectiveUnit(objective) {
    return objective.isConsumption()
        ? objective.indicator()?.get('unit') || ''
        : WiseMetering.getCurrency();
}

// As the legacy objective widgets do: the current month's target is prorated by
// the day of the month. A past year counts as complete, a future one as not
// started.
export function objectiveProgress(objective, period, now = moment()) {
    const
        year = objective.get('year'),
        targets = objectiveTargets(objective),
        real = Array.from({ length: 12 }, (_, month) => objective.getRealDataArray()?.[month] ?? null);

    let currentMonth, elapsed;

    if (year < now.year()) {
        currentMonth = 11;
        elapsed = 1;
    } else if (year > now.year()) {
        currentMonth = -1;
        elapsed = 0;
    } else {
        currentMonth = now.month();
        elapsed = now.date() / now.daysInMonth();
    }

    const firstMonth = period === 'mtd' ? Math.max(currentMonth, 0) : 0;

    let actual = 0, target = 0;

    for (let month = firstMonth; month <= currentMonth; month++) {
        actual += real[month] || 0;
        target += month === currentMonth ? targets[month] * elapsed : targets[month];
    }

    return {
        actual,
        currentMonth,
        deviation: target ? (actual - target) / target : null,
        real,
        target,
        targets
    };
}

// Digital inputs and actuators both record their state through this kind.
export const STATE_KIND_SLUG = 'on_off';

export function isStateIndicator(indicator) {
    return indicator.get('kind_slug') === STATE_KIND_SLUG;
}

export const stateLabels = data => ({
    0: data?.labelForZero || i18next.t('common.off').capitalize(),
    1: data?.labelForOne || i18next.t('common.on').capitalize()
});

// Left out on purpose: `current` is mostly circuit phase current and
// `relative_humidity` mostly humidity, with only a few tanks among them.
const LEVEL_KIND_SLUGS = ['gas_linear', 'generic_value_linear', 'oil_linear', 'water_linear'];

export function isLevelIndicator(indicator) {
    return LEVEL_KIND_SLUGS.includes(indicator.get('kind_slug'));
}

// Discarded opportunities are not savings, so they are left out.
export const OPPORTUNITY_STATES = ['completed', 'ongoing', 'pending'];

// Consumption only adds up within one utility; cost and the number of
// opportunities (`count`) add up across them.
export const OPPORTUNITY_MEASURES = ['cost', 'consumption', 'count'];

export function scopedOpportunities({ siteId, utilityKindId }) {
    return WiseMetering.opportunities.filter(opportunity =>
        (!siteId || opportunity.get('site_id') === siteId) &&
        (!utilityKindId || opportunity.get('utility_kind_id') === utilityKindId));
}

export function opportunitySummary(opportunities, measure) {
    const totals = Object.fromEntries(OPPORTUNITY_STATES.map(state => [state, 0]));

    let openCount = 0;

    opportunities.forEach(opportunity => {
        const state = opportunity.get('state');

        if (!(state in totals)) {
            return;
        }

        totals[state] += measure === 'count' ? 1 : opportunity.get(measure) || 0;

        if (state !== 'completed') {
            openCount++;
        }
    });

    return {
        open: totals.ongoing + totals.pending,
        openCount,
        total: totals.completed + totals.ongoing + totals.pending,
        totals
    };
}

export function opportunityUnit({ measure, utilityKindId }) {
    switch (measure) {
        case 'consumption':
            return WiseMetering.utilityKinds.get(utilityKindId)?.unit() || '';
        case 'count':
            return '';
        default:
            return WiseMetering.getCurrency();
    }
}
